import React, { useState, useRef, useEffect } from "react";
import { DateTime } from "luxon";
// TODO: Add react-helmet-async when React 19 support is available for SEO meta tags

import { eventApi } from "../api/event";
import { useAuth } from "../contexts/AuthContext";
import { parseICS } from "../utils/iCalParser";
import { useNavigate } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import CustomFormInput from "../components/forms/CustomFormInput";
import "../pages/header.css";
import "../pages/Common.css"; /* Common styles for all pages */
import "../pages/Uploads.css";
import { useDevMode } from "../hooks/useDevMode";

/**
 * MIGRATION NOTE (2026-02-06): Migrated conflict resolution radio buttons to CustomFormInput
 * Part of DL-04FEB26-01: CustomFormInput migration
 */

const Uploads = ({ onEventsImported, initialEvents = [] }) => {
    const { user, loading, isAuthenticated } = useAuth(); // Use context
    const navigate = useNavigate();

    const [events, setEvents] = useState(initialEvents);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState("");
    const [selectedImages, setSelectedImages] = useState(new Map()); // eventId -> {file, compressedBase64, thumbnail}
    const [pendingConflicts, setPendingConflicts] = useState([]);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);
    const [resolvedConflicts, setResolvedConflicts] = useState([]);
    // const [user, setUser] = useState(null);

    const icsFileInputRef = useRef(null);

    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate("/login");
        }
    }, [loading, isAuthenticated, navigate]);

    const { isDevMode, isQualifiedUser } = useDevMode();

    // Add body class when dev mode is active
    useEffect(() => {
        if (isDevMode && isQualifiedUser) {
            document.body.classList.add('uploads-dev-mode-active');
        } else {
            document.body.classList.remove('uploads-dev-mode-active');
        }
        return () => document.body.classList.remove('uploads-dev-mode-active');
    }, [isDevMode, isQualifiedUser]);

    const handleRemoveEvent = (eventId) => {
        // Remove event from events list
        setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
        
        // Remove associated image from selectedImages Map
        setSelectedImages(prevImages => {
            const newImages = new Map(prevImages);
            newImages.delete(eventId);
            return newImages;
        });
        
        // Show success message
        setUploadStatus("Event removed from table");
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    const compressImage = (file, quality = 0.1) => {
        return new Promise(resolve => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions (max 800px width to match your mobile app sizing)
                const maxWidth = 800;
                const maxHeight = 600;
                let { width, height } = img;

                if (width > height) {
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);

                // Convert to base64 with compression (matching your mobile app's 0.1 quality)
                const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
                resolve(compressedBase64);
            };

            img.src = URL.createObjectURL(file);
        });
    };

    const handleImageUpload = async (eventId, file) => {
        if (!file) return;

        try {
            // Compress image similar to your mobile app
            const compressedBase64 = await compressImage(file, 0.1);

            // Create thumbnail for table display
            const thumbnailBase64 = await compressImage(file, 0.3); // Higher quality for thumbnail

            // Store in selectedImages map
            setSelectedImages(
                prev =>
                    new Map(
                        prev.set(eventId, {
                            file,
                            compressedBase64,
                            thumbnail: thumbnailBase64,
                            // fileName: `event_${eventId}.jpg`,
                        })
                    )
            ); // No status update needed here, as per request.
        } catch (error) {
            console.error("Error compressing image:", error); // Keep error logging for debugging
        }
    };

    const uploadEventsToServer = async () => {
        if (events.length === 0) {
            setUploadStatus("No events to upload");
            return;
        }

        setIsUploading(true);
        setShowUploadModal(true);

        // Initialize progress tracking
        setUploadProgress(
            events.map(event => ({
                id: event.id,
                title: event.title,
                status: "pending",
                hasImage: selectedImages.has(event.id),
            }))
        );

        try {
            // Separate events by type
            const newEvents = events.filter(event => {
                const resolution = resolvedConflicts.find(r => r.newEvent.id === event.id);
                return !resolution; // No resolution = new event
            });

            const updateEvents = events
                .filter(event => {
                    const resolution = resolvedConflicts.find(r => r.newEvent.id === event.id);
                    return resolution && (resolution.action === "overwrite" || resolution.action === "update");
                })
                .map(event => {
                    const resolution = resolvedConflicts.find(r => r.newEvent.id === event.id);
                    return {
                        ...event,
                        existingEventId: resolution.existingEvent.id,
                        conflictAction: resolution.action,
                    };
                });

            // Prepare images for new events
            const newEventsWithImages = newEvents.map(event => {
                const imageData = selectedImages.get(event.id);
                const eventToUpload = { ...event };

                if (imageData && imageData.compressedBase64) {
                    eventToUpload.base64Image = imageData.compressedBase64.split(",")[1];
                }

                return eventToUpload;
            });

            // Prepare images for update events
            const updateEventsWithImages = updateEvents.map(event => {
                const imageData = selectedImages.get(event.id);
                const eventToUpload = { ...event };

                if (imageData && imageData.compressedBase64) {
                    eventToUpload.base64Image = imageData.compressedBase64.split(",")[1];
                }

                return eventToUpload;
            });

            // Send to different endpoints
            let allResults = [];

            if (newEvents.length > 0) {
                const newResponse = await eventApi.importIcalEvents(newEventsWithImages);

                // Handle different response statuses.
                // The import handler has no partial-success path: it returns 400, 200
                // or 500 only (ical.controller.js:24-140). 207 Multi-Status comes from
                // the /update handler alone — see the update branch below.
                if (!newResponse.ok) {
                    // HTTP 500 = complete failure
                    if (newResponse.status === 500) {
                        const errorMsg = newResponse.data.message || "All event imports failed";
                        console.error("❌ FRONTEND: Complete failure for new events:", newResponse.data);
                        throw new Error(errorMsg);
                    }
                    // Other errors
                    throw new Error(`Server error for new events: ${newResponse.status}`);
                } else {
                    // HTTP 200 = complete success
                    allResults = allResults.concat(newResponse.data.events);
                }
            }

            if (updateEvents.length > 0) {
                const updateResponse = await eventApi.updateIcalEvents(updateEventsWithImages);

                // Handle different response statuses.
                // 207 MUST be checked before `ok`: axios resolves every 2xx status
                // (207 included), so compatClient stamps it { ok: true }. Nesting the
                // 207 branch under `!ok` made it unreachable, and partial failures
                // silently fell through to the 200 path with no warning to the user.
                if (updateResponse.status === 207) {
                    // HTTP 207 Multi-Status = partial failure (some succeeded, some failed)
                    console.warn("⚠️ FRONTEND: Partial failure for updates:", updateResponse.data);

                    // Use successful events from response
                    if (updateResponse.data.events && Array.isArray(updateResponse.data.events)) {
                        allResults = allResults.concat(updateResponse.data.events);
                    }

                    // Counts live under the V2 error envelope at error.details — NOT
                    // data.details, which is undefined and yields "0 succeeded, 0 failed"
                    // (ical.controller.js:247).
                    const errorDetails = updateResponse.data.error?.details || {};
                    const failedCount = errorDetails.failedEvents || 0;
                    const successCount = errorDetails.successfulEvents || 0;

                    // Show user-friendly message
                    setUploadStatus(
                        `${updateResponse.data.message || `Partially completed: ${successCount} succeeded, ${failedCount} failed`}`
                    );
                } else if (!updateResponse.ok) {
                    // HTTP 500 = complete failure
                    if (updateResponse.status === 500) {
                        const errorMsg = updateResponse.data.message || "All event updates failed";
                        console.error("❌ FRONTEND: Complete failure for updates:", updateResponse.data);
                        throw new Error(errorMsg);
                    }
                    // Other errors
                    throw new Error(`Server error for updates: ${updateResponse.status}`);
                } else {
                    // HTTP 200 = complete success
                    allResults = allResults.concat(updateResponse.data.events);
                }
            }

            // Update progress with event IDs from backend
            setUploadProgress(prev =>
                prev.map((item, index) => ({
                    ...item,
                    status: "completed",
                    eventId: allResults[index]?.eventId,
                }))
            );

            // Start polling for image upload status if there are events with images
            const eventsWithImagesCheck = events.filter(event => selectedImages.has(event.id));
            if (eventsWithImagesCheck.length > 0) {
                const eventIds = allResults.map(e => e.eventId);
                pollImageUploadStatus(eventIds);
            } else {
                setIsUploading(false);
            }
        } catch (error) {
            console.error("Error uploading events:", error);

            // Set user-friendly error message
            const errorMessage = error.message || "Failed to upload events. Please try again.";
            setUploadStatus(`❌ ${errorMessage}`);

            setUploadProgress(prev =>
                prev.map(item => ({
                    ...item,
                    status: "failed",
                }))
            );
            setIsUploading(false);
        }
    };

    const pollImageUploadStatus = async eventIds => {
        const maxAttempts = 15;
        const pollInterval = 2000;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const statuses = await eventApi.getUploadStatus(eventIds);

                setUploadProgress(prev =>
                    prev.map(item => {
                        const backendStatus = statuses.find(s => s.eventId === item.eventId);
                        if (backendStatus && item.hasImage) {
                            return {
                                ...item,
                                imageStatus: backendStatus.imageUploadStatus,
                                imageError: backendStatus.imageUploadError,
                            };
                        }
                        return item;
                    })
                );

                // Check if all uploads are complete
                const allComplete = statuses.every(
                    s =>
                        s.imageUploadStatus === "completed" ||
                        s.imageUploadStatus === "failed" ||
                        s.imageUploadStatus === "none"
                );

                if (allComplete) break;
            } catch (error) {
                console.error("Error polling upload status:", error);
            }

            await new Promise(resolve => setTimeout(resolve, pollInterval));
        }

        setIsUploading(false);
    };

    // Process the iCalendar file
    const processICalFile = async content => {
        console.log("Processing iCal file, length:", content.length);

        setIsUploading(true);
        setUploadStatus("Processing iCalendar file...");

        try {
            // Parse iCalendar data using our custom parser
            const parsedEvents = parseICS(content);

            // Filter to only Facebook events
            const facebookEvents = parsedEvents.filter(
                event => event.url && event.url.includes("facebook.com/events/")
            );

            if (facebookEvents.length === 0) {
                setUploadStatus("No Facebook events found. Please upload a Facebook iCalendar file.");
                return;
            }

            // Transform Facebook events to our app format
            const formattedEvents = facebookEvents.map(event => {
                const extractedFbId = event.url.split("/events/")[1].split("/")[0];

                return {
                    id: crypto.randomUUID(), // This is a temporary ID to match images with events to send to the back end
                    icalUID: event.id, // Store separately for duplicate detection (parser stores UID as 'id')
                    title: event.summary || "Title not parsed in processICalFile",
                    description: event.description || "",
                    // Return plain ISO strings - backend expects this format
                    eventStartDateTime: event.start.toISOString(),
                    eventEndDateTime: (event.end || new Date(event.start.getTime() + 2 * 60 * 60 * 1000)).toISOString(),
                    eventLocation: {
                        description: event.location || "Location TBA",
                        latitude: null,
                        longitude: null,
                        isVenueOnly: isVenueOnlyLocation(event.location),
                        rawLocation: event.location,
                    },
                    danceStyles: detectDanceStyles(event.summary, event.description),
                    isEventOwner: false,
                    facebookUrl: event.url,
                    facebookEventId: extractedFbId, // Keep the base FB event ID for reference
                    importSource: "ical",
                };
            });

            // Check if the single event is in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const eventDate = new Date(formattedEvents[0].eventStartDateTime);
            eventDate.setHours(0, 0, 0, 0);

            if (eventDate < today) {
                setUploadStatus(
                    `Error: This event is in the past (${eventDate.toLocaleDateString()}). Please check your file.`
                );
                return; // Reject the file
            }

            // Check if events already exist in frontend table based on icalUID or facebookEventId
            const alreadyInTable = formattedEvents.filter(event =>
                events.some(
                    existingEvent =>
                        existingEvent.icalUID === event.icalUID ||
                        (existingEvent.facebookEventId && existingEvent.facebookEventId === event.facebookEventId)
                )
            );

            if (alreadyInTable.length > 0) {
                const duplicateNames = alreadyInTable.map(e => e.title).join(", ");
                setUploadStatus(
                    `Event(s) already in table: ${duplicateNames}. This file contains events you've already loaded.`
                );
                return; // Stop processing
            }

            // Create lightweight check data for backend duplicate detection
            const eventChecks = formattedEvents.map(event => {
                const facebookId = event.facebookUrl ? event.facebookUrl.split("/events/")[1]?.split("/")[0] : null;

                return {
                    title: event.title,
                    date: event.eventStartDateTime,
                    facebookId: facebookId,
                    icalUID: event.icalUID,
                };
            });

            console.log("Checking", eventChecks.length, "events for duplicates");

            // Check for duplicates with backend (V2 API)
            const duplicateResults = await eventApi.checkDuplicates(eventChecks);

            // Process results
            const conflicts = [];
            const uniqueEvents = [];

            formattedEvents.forEach((event, index) => {
                const result = duplicateResults[index];
                if (result.isDuplicate) {
                    conflicts.push({
                        newEvent: event,
                        existingEvent: result.existingEvent,
                        conflictType: determineConflictType(event, result.existingEvent),
                        lastUpdated:
                            result.existingEvent?.lastModified ||
                            result.existingEvent?.updatedAt ||
                            result.existingEvent?.importHistory?.[0]?.importDate ||
                            result.existingEvent?.createdAt ||
                            "Unknown",
                        existingSource: result.existingEvent?.importSource || "unknown",
                    });
                } else {
                    uniqueEvents.push(event);
                }
            });

            // Handle conflicts or add unique events
            if (conflicts.length > 0) {
                setPendingConflicts(conflicts);
                setShowConflictModal(true);
                setUploadStatus(`Found ${conflicts.length} potential conflicts. Please review.`);
            } else {
                const mergedEvents = mergeEvents(events, uniqueEvents);
                setEvents(mergedEvents);
                
                // Check for venue-only locations and warn user
                const venueOnlyEvents = uniqueEvents.filter(event => event.eventLocation?.isVenueOnly);
                if (venueOnlyEvents.length > 0) {
                    setUploadStatus(`Added ${uniqueEvents.length} new events. Warning: ${venueOnlyEvents.length} event(s) have venue-only locations that may need manual verification.`);
                } else {
                    setUploadStatus(`Added ${uniqueEvents.length} new events.`);
                }
            }

            // Send events back to parent component
            if (onEventsImported) {
                onEventsImported(events);
            }
        } catch (error) {
            console.error("Error processing iCalendar file:", error);
            setUploadStatus(`Error: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleConflictResolutions = resolutions => {
        // Store the resolutions for later use
        setResolvedConflicts(resolutions);

        const eventsToAdd = resolutions
            .filter(r => r.action === "overwrite" || r.action === "update")
            .map(r => r.newEvent);

        const mergedEvents = mergeEvents(events, eventsToAdd);
        setEvents(mergedEvents);

        setShowConflictModal(false);
        setPendingConflicts([]);
        setUploadStatus(`Processed ${eventsToAdd.length} conflict resolutions.`);
    };

    const determineConflictType = (newEvent, existingEvent) => {
        // Same Facebook event from different import sources
        if (newEvent.facebookUrl && existingEvent.facebookEventId) {
            const newFbId = newEvent.facebookUrl.split("/events/")[1]?.split("/")[0];
            if (existingEvent.facebookEventId === newFbId) {
                return "facebook_duplicate";
            }
        }

        // Same iCal UID (updated event)
        if (newEvent.icalUID && existingEvent.icalUID === newEvent.icalUID) {
            return "ical_update";
        }

        // Potential duplicate based on title/date
        return "potential_duplicate";
    };

    const ConflictModal = ({ conflicts, onResolve }) => {
        const [resolutions, setResolutions] = useState(
            conflicts.map(conflict => ({
                ...conflict,
                action: "skip", // 'skip', 'overwrite', 'update'
            }))
        );

        const updateResolution = (index, action) => {
            const newResolutions = [...resolutions];
            newResolutions[index].action = action;
            setResolutions(newResolutions);
        };

        const getConflictMessage = conflict => {
            switch (conflict.conflictType) {
                case "past_event":
                    return `This event is in the past (${new Date(conflict.newEvent.eventStartDateTime).toLocaleDateString()}). Please check your file.`;
                case "facebook_duplicate":
                    return `This Facebook event already exists (imported via ${conflict.existingSource})`;
                case "ical_update":
                    return `This event may have been updated since last import`;
                case "potential_duplicate":
                    return `Potential duplicate found based on title and date`;
                default:
                    return `Conflict detected`;
            }
        };

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>Event Conflicts Detected</h3>
                    {resolutions.map((resolution, index) => (
                        <div key={index} className="conflict-item">
                            <h4>{resolution.newEvent.title}</h4>
                            <p className="conflict-message">{getConflictMessage(resolution)}</p>
                            <p className="conflict-details">
                                Existing: {resolution.existingSource} (Last updated:{" "}
                                {new Date(resolution.lastUpdated).toLocaleDateString()})
                                {/* <br />
                                New: iCalendar import */}
                            </p>

                            <div className="conflict-actions">
                                <CustomFormInput
                                    type="radio"
                                    variant="turquoise"
                                    layout="standard"
                                    name={`conflict-${index}`}
                                    value="skip"
                                    label="Skip (keep existing)"
                                    checked={resolution.action === "skip"}
                                    onChange={() => updateResolution(index, "skip")}
                                />
                                <CustomFormInput
                                    type="radio"
                                    variant="orange"
                                    layout="standard"
                                    name={`conflict-${index}`}
                                    value="overwrite"
                                    label="Overwrite with iCal data"
                                    checked={resolution.action === "overwrite"}
                                    onChange={() => updateResolution(index, "overwrite")}
                                />
                                {resolution.conflictType === "ical_update" && (
                                    <CustomFormInput
                                        type="radio"
                                        variant="purple"
                                        layout="standard"
                                        name={`conflict-${index}`}
                                        value="update"
                                        label="Update existing event"
                                        checked={resolution.action === "update"}
                                        onChange={() => updateResolution(index, "update")}
                                    />
                                )}
                            </div>
                        </div>
                    ))}

                    <div className="modal-actions">
                        <button onClick={() => onResolve(resolutions)} className="btn-turquoise">Apply Decisions</button>
                        <button onClick={() => onResolve([])} className="btn-orange">Cancel</button>
                    </div>
                </div>
            </div>
        );
    };

    // Check if location is venue-only (no address components)
    const isVenueOnlyLocation = (location) => {
        if (!location || location === "Location TBA") return false;
        
        // Check if location looks like just a venue name (no address indicators)
        const addressIndicators = [',', 'street', 'st ', 'avenue', 'ave ', 'road', 'rd ', 'drive', 'dr ', 'lane', 'ln ', 'boulevard', 'blvd', 'way', 'place', 'pl ', 'court', 'ct ', 'circle', 'cir', 'square', 'sq '];
        const locationLower = location.toLowerCase();
        
        // If no address indicators found, likely venue-only
        return !addressIndicators.some(indicator => locationLower.includes(indicator));
    };

    // Detect dance styles based on event title and description
    const detectDanceStyles = (title = "", description = "") => {
        const content = (title + " " + description).toLowerCase();
        const styles = [];

        if (content.includes("salsa")) styles.push("salsa");
        if (content.includes("bachata")) styles.push("bachata");
        if (content.includes("zouk")) styles.push("zouk");
        if (content.includes("kizomba")) styles.push("kizomba");
        if (content.includes("tango")) styles.push("tango");

        // Default to 'other' if no styles detected
        if (styles.length === 0) styles.push("other");

        return styles;
    };

    // Merge new events with existing events, avoiding duplicates
    const mergeEvents = (existingEvents, newEvents) => {
        const eventMap = new Map();

        // Add new events first so they appear at the top
        newEvents.forEach(event => {
            eventMap.set(event.id, event);
        });

        // Add existing events to map
        existingEvents.forEach(event => {
            eventMap.set(event.id, event);
        });

        return Array.from(eventMap.values());
    };

    // Clear the file input after processing so user can add more events
    const handleFileSelect = e => {
        const file = e.target.files[0];
        if (!file) return;

        console.log("Loading iCal file:", file.name);

        setUploadStatus(`Reading file: ${file.name}`);

        const reader = new FileReader();
        reader.onload = e => {
            const content = e.target.result;
            processICalFile(content);

            // Clear the file input so user can select another file
            e.target.value = "";
        };

        reader.onerror = e => {
            setUploadStatus(`Error reading file: ${e.target.error}`);
        };

        reader.readAsText(file);
    };

    // Date formatter that shows single date or date range for multi-day events
    const formatDate = (startDateString, endDateString) => {
        try {
            const startDate = DateTime.fromISO(startDateString);
            const endDate = DateTime.fromISO(endDateString);

            // Check if it's the same day
            if (startDate.hasSame(endDate, "day")) {
                // Single day event - show just the start date and time
                return startDate.toFormat("ccc, dd MMM yyyy HH:mm");
            } else {
                // Multi-day event - show date range
                const startFormatted = startDate.toFormat("ccc, dd MMM yyyy HH:mm");
                const endFormatted = endDate.toFormat("ccc, dd MMM yyyy HH:mm");
                return `${startFormatted}\nto\n${endFormatted}`;
            }
        } catch (error) {
            return "Invalid date";
        }
    };

    return (
        <div className="dashboard-container uploads">
            {/* TODO: Add SEO meta tags when react-helmet-async supports React 19
                Title: "Upload Events - Dance Localé"
                OG Title: "Upload Events - Dance Localé" */}
            {/* Header using the same structure as home/login pages */}
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="dashboard-header-card">
                        <div className="page-header-with-status">
                            <h2 className="page-title">Upload Facebook Events via iCalendar</h2>
                            {isQualifiedUser && isDevMode && (
                                <span className="dev-mode-status-indicator">
                                    🧪 DEV MODE ACTIVE
                                </span>
                            )}
                        </div>

                        {/* File upload section */}
                        <div className="uploads-section">
                            <div className="uploads-instructions">
                                <h3 className="uploads-instructions-title">Quick Setup Instructions</h3>
                                <div className="uploads-instructions-list">
                                    <div className="uploads-instruction-item">
                                        <span className="uploads-instruction-number">1.</span>
                                        <span>Download the iCalendar file from the Facebook event</span>
                                        <button className="uploads-details-link">Click here for details</button>
                                    </div>
                                    <div className="uploads-instruction-item">
                                        <span className="uploads-instruction-number">2.</span>
                                        <span>
                                            Click on "Upload Image" in the table to associate an image with each event
                                        </span>
                                    </div>
                                    <div className="uploads-instruction-item">
                                        <span className="uploads-instruction-number">3.</span>
                                        <span>
                                            Once you have 2-5 events preloaded, press the "Upload to App" button
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="uploads-upload-wrapper">
                                <div className="uploads-file-upload">
                                    <input
                                        ref={icsFileInputRef}
                                        type="file"
                                        accept=".ics,text/calendar"
                                        onChange={handleFileSelect}
                                        disabled={isUploading}
                                        className="uploads-file-input"
                                        id="ics-file-input"
                                    />
                                    <label htmlFor="ics-file-input" className="btn-purple">
                                        Browse for iCalendar File
                                    </label>
                                </div>

                                {/* Status display */}
                                {uploadStatus && (
                                    <div
                                        className={`status-message ${uploadStatus.includes("Error") ? "status-error" : "status-success"}`}>
                                        {isUploading && <div className="spinner"></div>}
                                        {uploadStatus}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Imported events table */}
                        <div className="uploads-section">
                            <div className="uploads-section-header">
                                <h3 className="section-title">Events ({events.length})</h3>
                                <div className="uploads-header-actions">
                                    <button
                                        onClick={uploadEventsToServer}
                                        disabled={isUploading || events.length === 0}
                                        className="btn-turquoise btn-large">
                                        {isUploading
                                            ? "Processing..."
                                            : `Upload ${events.length} events to Dance Localé App`}
                                    </button>

                                    {/* Clear all events button */}
                                    <button
                                        onClick={() => {
                                            setEvents([]);
                                            setSelectedImages(new Map());
                                            setUploadStatus("Cleared all events");
                                            if (icsFileInputRef.current) icsFileInputRef.current.value = "";
                                        }}
                                        className="btn-gray">
                                        Clear All
                                    </button>
                                </div>
                            </div>

                            {/* Combined events table */}
                            {events.length > 0 ? (
                                <div className="uploads-table-wrapper">
                                    <table className="uploads-table">
                                        <thead>
                                            <tr>
                                                <th>Image</th>
                                                <th>Title</th>
                                                <th>Start Date</th>
                                                <th>Location</th>
                                                <th>Dance Styles</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {events.map(event => (
                                                <tr key={event.id}>
                                                    <td className="uploads-image-cell">
                                                        <div className="uploads-image-container">
                                                            {selectedImages.has(event.id) ? (
                                                                <>
                                                                    <img
                                                                        src={selectedImages.get(event.id).thumbnail}
                                                                        alt="Event thumbnail"
                                                                        className="uploads-thumbnail"
                                                                    />
                                                                    <span
                                                                        className="uploads-change-image-link"
                                                                        onClick={() =>
                                                                            document
                                                                                .getElementById(`image-${event.id}`)
                                                                                .click()
                                                                        }>
                                                                        Change
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <label
                                                                    htmlFor={`image-${event.id}`}
                                                                    className="uploads-image-placeholder">
                                                                    <span>📷</span>
                                                                    <small>
                                                                        Click to Upload <br /> Image
                                                                    </small>
                                                                </label>
                                                            )}
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={e =>
                                                                    handleImageUpload(event.id, e.target.files[0])
                                                                }
                                                                className="uploads-image-input"
                                                                id={`image-${event.id}`}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>{event.title}</td>
                                                    <td style={{ whiteSpace: "pre-line" }}>
                                                        {formatDate(
                                                            event.eventStartDateTime,
                                                            event.eventEndDateTime
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="location-display">
                                                            {event.eventLocation?.description || "N/A"}
                                                            {event.eventLocation?.isVenueOnly && (
                                                                <div className="location-warning">
                                                                    ⚠️ Venue only - may need manual location verification
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{event.danceStyles.join(", ")}</td>
                                                    <td>
                                                        <button
                                                            onClick={() => handleRemoveEvent(event.id)}
                                                            className="btn-orange btn-small"
                                                            title="Remove this event from table">
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="uploads-no-events">
                                    No events imported yet. Upload an iCalendar file to get started.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showConflictModal && <ConflictModal conflicts={pendingConflicts} onResolve={handleConflictResolutions} />}
            {showUploadModal && (
                <div className="modal-overlay">
                    <div className="upload-progress-modal">
                        <h3>Uploading Events</h3>
                        <div className="progress-list">
                            {uploadProgress.map((item, index) => (
                                <div key={index} className="progress-item">
                                    <div className="progress-status">
                                        {item.status === "completed" && !item.hasImage && "✅"}
                                        {item.status === "completed" &&
                                            item.hasImage &&
                                            item.imageStatus === "completed" &&
                                            "✅"}
                                        {item.status === "completed" &&
                                            item.hasImage &&
                                            item.imageStatus === "uploading" &&
                                            "🔄"}
                                        {(item.status === "failed" || item.imageStatus === "failed") && "❌"}
                                        {item.status === "pending" && "⏳"}
                                    </div>
                                    <div className="progress-details">
                                        <div className="event-title">{item.title}</div>
                                        {item.hasImage && item.imageStatus === "uploading" && (
                                            <div className="upload-status">Uploading image...</div>
                                        )}
                                        {item.imageStatus === "failed" && (
                                            <div className="error-status">Image upload failed</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="modal-actions">
                            {isUploading ? (
                                <>
                                    <button
                                        onClick={() => {
                                            setIsUploading(false);
                                            setShowUploadModal(false);
                                            setUploadStatus("Upload cancelled by user");
                                        }}
                                        className="btn-orange">
                                        Cancel Upload
                                    </button>
                                    <button disabled className="btn-turquoise">
                                        Uploading... Please Wait
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => {
                                            const hasErrors = uploadProgress.some(item => 
                                                item.status === "failed" || item.imageStatus === "failed"
                                            );
                                            if (hasErrors) {
                                                setUploadStatus("Some uploads failed. Check the progress above for details.");
                                            } else {
                                                setUploadStatus("All uploads completed successfully!");
                                            }
                                            setShowUploadModal(false);
                                            setEvents([]);
                                            setSelectedImages(new Map());
                                            setUploadProgress([]);
                                        }}
                                        className="btn-turquoise">
                                        Done
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            setUploadProgress([]);
                                        }}
                                        className="btn-orange">
                                        Keep Events in Table
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Uploads;
